/* eslint-disable no-else-return */
/* eslint-disable class-methods-use-this */
import * as THREE from 'three';
import * as marietta from '../..';
import { LinkType } from '../collection/ItemInfoCollection';
import { formatDiagnosticsWithColorAndContext } from 'typescript';
import { ArticleDocuments, ImageType, ResImportPins, ResRegisterPin, ResSaveImage } from './interface/APIManager';

/** リクエスト内容が不正な場合のHTTPステータスコード */
const HTTP_STATUS_BAD_REQUEST = 400;

export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | '';
export type ResponseType = 'JSON' | 'BLOB' | '';
export interface BlobResponse {
  data: Blob;
  statusCode: number;
  message: string;
}

/** 企画ドキュメント選択時の絞り込み条件 */
export interface PlanningDocumentFilter {
  /** 使用済みとして除外する画像のリンク */
  excludeLinks?: LinkType[];
  /** 画像ファイルのみ表示するか */
  imageOnly?: boolean;
}

/**
 * 共有リンク発行APIのレスポンス
 */
export interface ResShareLink {
  /** 共有用のURL */
  share_url: string;
  /** 閲覧可能期限 */
  expires_at: string;
}

/**
 * 共有リンク発行APIの結果
 */
export interface ResShareLinkResult {
  /** 発行された共有リンク情報（発行できなかった場合は null） */
  shareLink: ResShareLink | null;
  /** 保存データが存在しないため発行できなかった */
  isNoSavedData: boolean;
}

export interface APIBase {
  readonly url: string;

  readonly method: HTTPMethod;

  readonly header: { [key: string]: string | number | undefined };

  action: <TResponse>() => Promise<TResponse>;
}

export default class APIManager implements APIBase {
  public url: string;

  public method: HTTPMethod;

  public responseType: ResponseType;

  constructor(url: string, method: HTTPMethod, responseType: ResponseType) {
    this.url = url;
    this.method = method;
    this.responseType = responseType;
  }

  get header() {
    return {
      Authorization: marietta.webcad.WebCADGlobal.webcad2Dmanager.access_token,
    };
  }

  action<TResponse>(): Promise<TResponse> {
    return new Promise<TResponse>((resolve) => {
      if (this.method === 'GET') {
        const apiUrl = this.url;
        fetch(apiUrl, {
          method: 'GET',
          headers: this.header,
        })
          .then((response) => {
            if (response.status >= 200 && response.status <= 299) {
              if (this.responseType === 'BLOB') {
                return response.blob();
              } else {
                return response.json();
              }
            }
            throw Error(response.statusText);
          })
          .then((blob) => {
            resolve({ data: blob, status: 200 } as any);
          })
          .catch((res) => {
            resolve(res as any);
          });
      }
    });
  }

  /**
   * 共有リンク発行API
   * モデルを共有するためのURLと閲覧可能期限を発行する
   */
  public static async createShareLink(): Promise<ResShareLinkResult> {
    /** 操作種別（1：企画管理、2：既存管理）。API が共有リンク発行の操作ログに使う */
    const OPERATION_TYPE_KEY: string = 'operation_type';
    const { webcad2Dmanager } = marietta.webcad.WebCADGlobal;
    const url = `${webcad2Dmanager.domain}/api/v1/articles/${webcad2Dmanager.article_id}/compartments/${webcad2Dmanager.division_id}/plans/${webcad2Dmanager.zumen_id}/share-links`;
    const token = webcad2Dmanager.access_token;
    const formData: FormData = new FormData();
    formData.append(OPERATION_TYPE_KEY, webcad2Dmanager.operation_type.toString());

    try {
      const res = await fetch(url, { method: 'POST', headers: { Authorization: token }, body: formData });
      if (!res.ok) {
        console.error(`${res.status}: ${res.statusText}`);
        // 保存データが存在しないプランはリンクを発行できず、APIが400を返す
        return { shareLink: null, isNoSavedData: res.status === HTTP_STATUS_BAD_REQUEST };
      }
      const json = await res.json();
      if (json.errors) {
        console.error(json.errors);
        return { shareLink: null, isNoSavedData: false };
      }
      return { shareLink: json, isNoSavedData: false };
    } catch (err) {
      console.error(err);
      return { shareLink: null, isNoSavedData: false };
    }
  }

  /**
   * ピン削除API
   * @param pinId pin_id
   */
  public static async deletePin(pinId: string): Promise<void> {
    const url = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.domain}/api/v1/webcad/data/${marietta.webcad.WebCADGlobal.webcad2Dmanager.zumen_id}/pins/${pinId}`;
    const token = marietta.webcad.WebCADGlobal.webcad2Dmanager.access_token;
    try {
      await fetch(url, { method: 'DELETE', headers: { Authorization: token }});
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * ピン登録API
   * @param pin pin情報
   */
  public static async registerOrUpdatePin(pin: marietta.webcad.item.PinPicture): Promise<ResRegisterPin> | null {
    const info = pin.itemInfo;
    /** ピン登録URL */
    const registerUrl = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.domain}/api/v1/webcad/data/${marietta.webcad.WebCADGlobal.webcad2Dmanager.zumen_id}/pins`;
    /** ピン更新URL */
    const updateUrl = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.domain}/api/v1/webcad/data/${marietta.webcad.WebCADGlobal.webcad2Dmanager.zumen_id}/pins/${info.pin_id}`;
    const token = marietta.webcad.WebCADGlobal.webcad2Dmanager.access_token;

    try {
      const formData = await this.convertPinToFormData(info);
      // pin_idが空文字の場合は登録、それ以外は更新
      const res = await fetch(info.pin_id ? updateUrl : registerUrl, { method: 'POST', headers: { Authorization: token }, body: formData });
      const json = await res.json();
      if (json.errors) {
        console.error(json.errors);
        return null;
      }
      return json;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  /**
   * 登録するピンデータをformDataに変換
   */
  private static async convertPinToFormData(pinData: marietta.webcad.collection.ItemInfoCollection): Promise<FormData> {
    // 付帯工事があるかどうか
    const hasAuxiliaryConstruction = pinData.auxiliaryData !== undefined && pinData.auxiliaryData !== null;
    const formData = new FormData();
    // const rm = marietta.WebCADGlobal.webcad2Dmanager.getRoomWithPin(pinData); // ピンが配置してある部屋
    // formData.append('room_name', rm ? rm.itemInfo.text : '室外'); // 部屋名
    formData.append('room_name', `${pinData.auxiliaryData.auxiliaryDatum.room_name ?? ''}`); // 付帯工事名

    if (hasAuxiliaryConstruction) {
      // 付帯工事がある場合
      const auxiliaryDatum = pinData.auxiliaryData.auxiliaryDatum;
      const constructionName = auxiliaryDatum.other_flag === 1 ? auxiliaryDatum.otherText : auxiliaryDatum.construction;
      formData.append('auxiliary_construction_id', pinData.auxiliaryData.auxiliaryDatum.constructionId ?? ''); // 付帯工事ID
      formData.append('auxiliary_construction_name', constructionName ?? ''); // 付帯工事名
      formData.append('quantity', `${pinData.auxiliaryData.auxiliaryDatum.quantity ?? 0}`); // 見積もり数量
      formData.append('unit', pinData.auxiliaryData.auxiliaryDatum.unit ?? '');  // 単位
      formData.append('unit_price', `${pinData.auxiliaryData.auxiliaryDatum.unitPrice ?? 0}`); // 見積もり単価
      formData.append('total_price', `${pinData.auxiliaryData.auxiliaryDatum.totalPrice ?? 0}`); // 見積もり金額
      formData.append('debtor_burden_rate', `${pinData.auxiliaryData.auxiliaryDatum.rate ?? 0}`); // 債務負担率
      formData.append('repair_reason', pinData.auxiliaryData.auxiliaryDatum.repair ?? '');
    } else {
      // 付帯工事がない場合,必須項目のみ
      formData.append('auxiliary_construction_id', '');
      formData.append('auxiliary_construction_name', '');
      formData.append('quantity', `0`);
      formData.append('unit_price', `0`);
      formData.append('total_price', `0`);
    }

    // 備考 複数対応
    if( pinData.remarks.length ) {
      for(let i = 0; i < pinData.remarks.length; i++) {
        const remarks = pinData.remarks[i];
        const fieldName = `note[${i}]`;
        formData.append(fieldName, remarks.text);
      }
    } else {
      formData.append('note[0]', ''); // 備考
    }

    // before画像 複数対応
    if (pinData.link?.length) {
      for(let i = 0; i < pinData.link.length; i++) {
        const link = pinData.link[i];
        const fieldName = `before_image[${i}]`;
        if(link.binary) {
          const beforeBinary = await this.convertBinary(link.binary);
          if (beforeBinary) {
            const fileName = marietta.WebCADGlobal.webcad2Dmanager.getFileNameFromDataURL(link.binary);
            formData.append(fieldName, beforeBinary, fileName); // before画像
          }
        } else {
          const blob = await this.imageUrlToBlob(link.url);
          if (blob) {
            const fileName = marietta.WebCADGlobal.webcad2Dmanager.getFileName(link.name, link.url);
            formData.append(fieldName, blob, fileName);
          }
        }
      }
    } 

    // after画像 付帯工事がない＝after画像もない 複数対応
    if (hasAuxiliaryConstruction && pinData.after_link?.length) {
      for(let i = 0; i < pinData.link.length; i++) {
        const link = pinData.after_link[i];
        const fieldName = `after_image[${i}]`;
        if(link.binary) {
          const afterBinary = await this.convertBinary(link.binary);
          if (afterBinary) {
            const fileName = marietta.WebCADGlobal.webcad2Dmanager.getFileNameFromDataURL(link.binary);
            formData.append(fieldName, afterBinary, fileName); // after画像
          }
        } else {
          if(!link.url) {
            formData.append(fieldName, null);
            continue;
          }
          const blob = await this.imageUrlToBlob(link.url);
          if (blob) {
            const fileName = marietta.WebCADGlobal.webcad2Dmanager.getFileName(link.name, link.url);
            formData.append(fieldName, blob, fileName);
          }
        }
      }
    } 

    // その他フラグ
    if (!hasAuxiliaryConstruction) {
      formData.append('other_flag', '0');
    } else {
      formData.append('other_flag', `${pinData.auxiliaryData.auxiliaryDatum.other_flag ?? 0}`);
    }
    return formData;
  }

  /**
   * バイナリデータかチェック、バイナリデータでない場合は変換
   * @returns blob
   */
  private static async convertBinary(dataUrl: string): Promise<Blob | null> {
    if (dataUrl.includes(';base64')) {
      return marietta.WebCADGlobal.webcad2Dmanager.dataURLtoBlob(dataUrl);
    }
    // 元からURL変換しない
    // if (dataUrl.includes('http')) {
    //   try {
    //     const response = await fetch(dataUrl);
    //     const blob = await response.blob();
    //     return blob;
    //   } catch (err) {
    //     console.error(err);
    //   }
    // }
    return null;
  }

  private static async imageUrlToBlob(url: string): Promise<Blob> {
    try {
      const response = await fetch(url, { mode: "cors", cache: "reload" });
      // BLOB形式でレスポンスを取得
      const blob = await response.blob();

      return blob;

    } catch(e) {
      console.warn(e);
    }
  }

  /**
   * 写真ドキュメントの選択
   * @return void
   * @param callback
   */
  public static selectPictureDocument ( callback:Function ){
    marietta.webcad.WebCADGlobal.showBusy();
    const url = marietta.webcad.WebCADGlobal.webcad2Dmanager.get_document_api_url;
    const token = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.access_token}`;
    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: token,
      },
    }).then((response) => response.json())
      .then((data) => {
        const docList: ArticleDocuments[] = data.article_documents;
        if (Array.isArray(docList) && docList.length > 0) {
          const param = {docList: docList};
          const tarDialog = marietta.webcad.WebCADGlobal.showDialog(marietta.webcad.view.dialog.PlanDocumentListDialog, false, true, false, false, param);
          tarDialog.callBack = callback;
        } else {
          marietta.webcad.WebCADGlobal.showAlert(marietta.webcad.collection.WebCADMessages.getText('ERROR_NO_PLAN_DOCUMENT'));
        }
        marietta.webcad.WebCADGlobal.hideBusy();
      })
      .catch((error) => {
        console.error(error);
        marietta.webcad.WebCADGlobal.hideBusy();
      });
  }

  /**
   * 企画ドキュメント取得
   */
  public static async planningDocument(): Promise<ArticleDocuments[]> {
    marietta.webcad.WebCADGlobal.showBusy();
    const url = marietta.webcad.WebCADGlobal.webcad2Dmanager.get_planning_document_api_url;
    const token = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.access_token}`;

    try {

      const response = await fetch(url, { method: 'GET', headers: { Authorization: token } });
      const data = await response.json();
      const docList: ArticleDocuments[] = data.plan_documents;
      return docList;

    } catch(e) {

      console.error(e);

    } finally {

      marietta.webcad.WebCADGlobal.hideBusy();

    }
  }

  /**
   * 選択できるドキュメントだけに絞り込む
   * 使用済み判定は、ピン保存後にURLがピン画像のものへ差し替わるためファイル名でも突き合わせる
   */
  private static filterSelectableDocument(docList: ArticleDocuments[], filter: PlanningDocumentFilter): ArticleDocuments[] {
    const { excludeLinks = [], imageOnly = false } = filter;
    const isUsed = (doc: ArticleDocuments): boolean => excludeLinks.some((link) => (
      (!!link.name && link.name === doc.file_name) || (!!link.url && link.url === doc.file_path)
    ));
    const isImage = (doc: ArticleDocuments): boolean => marietta.webcad.util.FileUtil.isImageFile(doc.file_name || doc.file_path);
    return docList.filter((doc) => !isUsed(doc) && (!imageOnly || isImage(doc)));
  }

  /**
   * 企画ドキュメント
   * TODO: planningDocument関数を使用するようリファクタリングを実施。
   * API以外の処理も含まれているため、呼び出し元 or 他のクラスで共通化した方が良いと思われる
   */
  public static selectPlanningDocument(callback: Function, filter: PlanningDocumentFilter = {}) {
    marietta.webcad.WebCADGlobal.showBusy();
    const url = marietta.webcad.WebCADGlobal.webcad2Dmanager.get_planning_document_api_url;
    const token = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.access_token}`;
    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: token,
      },
    }).then((response) => response.json())
      .then((data) => {
        const allDocList = data.plan_documents;
        const hasDocument = Array.isArray(allDocList) && allDocList.length > 0;
        const docList = hasDocument ? APIManager.filterSelectableDocument(allDocList, filter) : allDocList;
        if (Array.isArray(docList) && docList.length > 0) {
          const param: any = {};
          param.docList = docList;
          const tarDialog = marietta.webcad.WebCADGlobal.showDialog(marietta.webcad.view.dialog.PlanDocumentListDialog, false, true, false, false, param);
          tarDialog.callBack = callback;
        } else {
          // 絞り込んだ結果0件になった場合は、ドキュメント自体が無い場合と区別してメッセージを出し分ける
          const messageKey = hasDocument ? 'ERROR_NO_SELECTABLE_PLAN_DOCUMENT' : 'ERROR_NO_PLAN_DOCUMENT';
          marietta.webcad.WebCADGlobal.showAlert(marietta.webcad.collection.WebCADMessages.getText(messageKey), marietta.webcad.collection.WebCADMessages.getText('ERROR_TITLE'), null, false);
        }
        marietta.webcad.WebCADGlobal.hideBusy();
      })
      .catch((error) => {
        console.error(error);
        marietta.webcad.WebCADGlobal.hideBusy();
      });
  }

  /**
   * API Name：  webcad企画ドキュメント保存
   * How to use：画像をバイナリデータで保存する
   * URI：/api/v1/webcad/plan-documents/{plan_id}
   */
  static async saveImage(imageData: File, imageType: ImageType): Promise<ResSaveImage> {
    const blob = imageData.slice(0, imageData.size, imageData.type); // ファイルの内容をBlobとして抽出

    const createFormData = (): FormData => {
      const formData = new FormData();
      const extension = imageData.name.split('.').pop();  // ex) 'sample.jpg' -> 'jpg'
      const fileNameWithoutExtension = imageData.name.split('.').slice(0, -1).join('.'); // ex) 'sample.jpg' -> 'sample'
      const fileName = `${fileNameWithoutExtension}_${(new Date()).getTime()}_${THREE.MathUtils.generateUUID()}.${extension}`;
      formData.append('image', blob, fileName);
      formData.append('type', `${imageType}`);
      return formData;
    }

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        Authorization: `${marietta.webcad.WebCADGlobal.webcad2Dmanager.access_token}`,
      },
      body: createFormData()
    };

    try {

      const url = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.domain}/api/v1/webcad/plan-documents/${marietta.webcad.WebCADGlobal.webcad2Dmanager.zumen_id}`;
      const response = await fetch(url, requestInit);
      const json = await response.json();

      if (json.errors) {
        throw new Error(JSON.stringify(json.errors));
      }

      return json;

    } catch(err) {

      console.error(err);

    }
  }

  /**
   * API Name：  ピンインポート
   * How to use：指定したピンのデータを別企画へインポートする
   * URI：/api/v1/webcad/data/{plan_id}/import
   * @param planId インポート先の企画ID
   * @param pinIds エクスポート元企画のピンID一覧
   */
  public static async importPins(planId: string, pinIds: string[]): Promise<ResImportPins | null> {
    if (!planId || !pinIds || pinIds.length < 1) return null;

    const url = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.domain}/api/v1/webcad/data/${planId}/import`;
    const token = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.access_token}`;

    const formData = new FormData();
    formData.append('pin_ids', pinIds.join(','));

    try {

      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: token },
        body: formData,
      });
      const json = await response.json();

      if (!response.ok || json.errors) {
        throw new Error(json.errors ? JSON.stringify(json.errors) : response.statusText);
      }

      return json;

    } catch (err) {

      console.error(err);
      return null;

    }
  }

}
